import { nowMs } from "@/utils/appNow"

// Fallback while the active weather run is unknown (no status yet, core unreachable).
export const STALENESS_REFERENCE = { mode: "age", thresholdHours: 24 }

// Reference from GET /weather/status ({ activeRun, activatedAt }); age-based without it.
export function stalenessReference(weatherStatus) {
  return weatherStatus?.activeRun
    ? { mode: "run", currentRun: weatherStatus.activeRun, activatedAt: weatherStatus.activatedAt ?? null }
    : STALENESS_REFERENCE
}

export const UNKNOWN_STALENESS = {
  status: "unknown",
  ageHours: null,
  calculatedAt: null,
}

// Same allowlist as core's POST /routes/{id}/recalculate.
const RECALC_STATES = new Set(["active", "suggested"])

export function canRecalcRouteState(state) {
  return RECALC_STATES.has(String(state ?? "").toLowerCase())
}

// { status: "fresh" | "stale" | "unknown", ageHours, calculatedAt, basis, routeRun, currentRun, activatedAt }
// basis: "run" (stamp vs. active run) | "activation" (unstamped vs. activatedAt) | "age"
export function routeStaleness(route, reference = STALENESS_REFERENCE) {
  const calculatedAt =
    route?.summary?.calculated_at ?? route?.timestamp ?? null
  const calculatedMs = calculatedAt ? new Date(calculatedAt).getTime() : NaN
  if (!Number.isFinite(calculatedMs)) return UNKNOWN_STALENESS

  const ageExactHours = Math.max(0, (nowMs() - calculatedMs) / 3600000)
  const ageHours = Math.floor(ageExactHours)
  if (reference?.mode === "run" && reference.currentRun) {
    const routeRun = route?.summary?.weather_run ?? null
    const run = {
      ageHours,
      calculatedAt,
      routeRun,
      currentRun: reference.currentRun,
      activatedAt: reference.activatedAt ?? null,
    }
    if (routeRun) {
      return { status: routeRun === reference.currentRun ? "fresh" : "stale", basis: "run", ...run }
    }
    // Routes from before the run stamp: calculated after the switch counts as current.
    const activatedMs = reference.activatedAt ? new Date(reference.activatedAt).getTime() : NaN
    if (Number.isFinite(activatedMs)) {
      return { status: calculatedMs >= activatedMs ? "fresh" : "stale", basis: "activation", ...run }
    }
  }
  const threshold =
    reference?.mode === "age" && reference.thresholdHours != null
      ? reference.thresholdHours
      : STALENESS_REFERENCE.thresholdHours
  // Compare the exact age so sub-hour thresholds work; ageHours stays whole for display.
  return { status: ageExactHours > threshold ? "stale" : "fresh", ageHours, calculatedAt, basis: "age" }
}
